import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Download, FileText } from 'lucide-react';

export default function Press() {
  const pressKit = [
    { name: 'High-Res Photos', size: '45 MB', type: 'ZIP' },
    { name: 'Bio & Press Release', size: '2 MB', type: 'PDF' },
    { name: 'Logo Pack', size: '8 MB', type: 'ZIP' },
    { name: 'Tour Dates', size: '500 KB', type: 'PDF' },
  ];

  const pressFeatures = [
    {
      outlet: 'Washington Post',
      title: 'Keeping Go-Go Alive: Scooby DaGodSon\'s Mission',
      date: 'December 2024',
      quote: 'A modern voice for a timeless genre'
    },
    {
      outlet: 'DMV Music Magazine',
      title: 'The New Face of Go-Go Music',
      date: 'November 2024',
      quote: 'Bridging generations through rhythm'
    },
    {
      outlet: 'Local Sounds',
      title: '10 DMV Artists to Watch in 2025',
      date: 'October 2024',
      quote: 'Authentic energy that moves crowds'
    },
  ];

  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero */}
      <motion.section
        className="py-32 px-6 bg-gradient-to-b from-[#0a0a0a] to-black"
        initial="hidden"
        animate="visible"
        variants={fadeInUp}
      >
        <div className="container mx-auto text-center">
          <FileText className="w-16 h-16 text-orange-500 mx-auto mb-6" />
          <h1 className="text-6xl md:text-7xl font-bold mb-6">
            <span className="text-orange-500">Press Kit</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Media resources and press information for Scooby DaGodSon
          </p>
        </div>
      </motion.section>

      {/* Press Kit Downloads */}
      <section className="py-20 px-6 bg-black">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-4xl font-bold mb-12 text-center">Download Press Materials</h2>
          <motion.div
            className="grid md:grid-cols-2 gap-6"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: { staggerChildren: 0.1 }
              }
            }}
          >
            {pressKit.map((item, index) => (
              <motion.div
                key={index}
                className="bg-[#1a1a1a] rounded-lg p-6 flex items-center justify-between hover:bg-[#252525] transition-colors"
                variants={fadeInUp}
              >
                <div className="flex items-center gap-4">
                  <div className="bg-orange-500/20 p-3 rounded-lg">
                    <FileText className="w-6 h-6 text-orange-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{item.name}</h3>
                    <p className="text-gray-400 text-sm">{item.type} • {item.size}</p>
                  </div>
                </div>
                <Button className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700">
                  <Download className="h-4 w-4" />
                </Button>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Press Features */}
      <motion.section
        className="py-20 px-6 bg-[#0a0a0a]"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeInUp}
      >
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-4xl font-bold mb-12 text-center">Recent Press</h2>
          <div className="space-y-8">
            {pressFeatures.map((feature, index) => (
              <div key={index} className="bg-[#1a1a1a] rounded-lg p-8">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                  <div>
                    <h3 className="text-2xl font-bold mb-2">{feature.title}</h3>
                    <p className="text-orange-500 font-semibold">{feature.outlet}</p>
                  </div>
                  <span className="text-gray-400 text-sm mt-2 md:mt-0">{feature.date}</span>
                </div>
                <p className="text-gray-300 italic border-l-4 border-orange-500 pl-4">
                  "{feature.quote}"
                </p>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Bio Section */}
      <motion.section
        className="py-20 px-6 bg-black"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeInUp}
      >
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-4xl font-bold mb-8">Artist Bio</h2>
          <div className="bg-[#1a1a1a] rounded-lg p-8 space-y-4 text-gray-300 leading-relaxed">
            <p>
              Scooby DaGodSon is a Washington DC-based go-go artist carrying forward the rich musical tradition of the DMV area. With over a decade of experience, Scooby has established himself as one of the most authentic voices in contemporary go-go music.
            </p>
            <p>
              His sound seamlessly blends the classic percussion-heavy beats and call-and-response style of traditional go-go with modern influences from hip-hop and R&B, creating a fresh yet respectful approach to the genre.
            </p>
            <p>
              Having performed at over 500 shows across the DMV region and amassed a dedicated following of 50,000+ fans, Scooby DaGodSon continues to bring communities together through the universal language of go-go music.
            </p>
          </div>
        </div>
      </motion.section>

      {/* Contact */}
      <motion.section
        className="py-20 px-6 bg-[#0a0a0a]"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeInUp}
      >
        <div className="container mx-auto text-center max-w-2xl">
          <h2 className="text-4xl font-bold mb-6">Press Inquiries</h2>
          <p className="text-gray-400 text-lg mb-8">
            For interviews, features, or press credentials, please contact:
          </p>
          <div className="bg-[#1a1a1a] rounded-lg p-8">
            <p className="text-xl font-semibold mb-2">Media Relations</p>
            <a href="mailto:press@scoobydagodson.com" className="text-orange-500 hover:text-orange-400 transition-colors">
              press@scoobydagodson.com
            </a>
            <p className="text-gray-400 mt-4">(202) 555-0123</p>
          </div>
        </div>
      </motion.section>
    </div>
  );
}
